import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { proAPI } from '../lib/api';
import { 
  Building2, 
  Package, 
  Warehouse, 
  Truck, 
  Camera, 
  Headphones, 
  Clock, 
  ShieldCheck, 
  BarChart3, 
  CheckCircle, 
  ArrowRight, 
  Phone, 
  Mail, 
  Star,
  Zap,
  Box,
  PackageCheck,
  Settings,
  Users,
  FileText,
  Gift
} from 'lucide-react';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export default function ProPage() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    volume: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      await proAPI.submitQuote(formData);
      toast.success('Demande envoyée ! Nous vous recontacterons sous 24h.');
      setFormData({ company: '', name: '', email: '', phone: '', volume: '', message: '' });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setSending(false);
    }
  };

  const services = [
    {
      icon: Warehouse,
      title: 'Stockage sécurisé',
      description: 'Utilisez notre entrepôt comme plateforme de stockage pour vos marchandises. Espace sécurisé à Béziers avec gestion optimisée de vos stocks.',
      features: ['Espace dédié', 'Inventaire temps réel', 'Assurance incluse', 'Tarif sur devis']
    },
    {
      icon: BarChart3,
      title: 'Gestion des flux',
      description: 'Optimisez votre logistique avec notre système de gestion des flux entrants et sortants. Suivi complet de vos expéditions.',
      features: ['Tableau de bord dédié', 'Rapports mensuels', 'Alertes automatiques', 'API disponible']
    },
    {
      icon: Zap,
      title: 'Envoi Express',
      description: 'Service prioritaire pour vos envois urgents. Prise en charge immédiate et expédition le jour même.',
      features: ['Expédition J+0', 'Suivi prioritaire', 'Notification client', 'Garantie livraison']
    },
    {
      icon: Camera,
      title: 'Photos & Contrôle qualité',
      description: 'Vérification visuelle de vos marchandises à réception. Photos HD envoyées avant expédition pour validation.',
      features: ['Photos HD', 'Contrôle quantité', 'Détection dommages', 'Rapport détaillé']
    },
    {
      icon: Headphones,
      title: 'Support dédié',
      description: 'Un interlocuteur unique pour votre compte. Réponse garantie sous 2h en semaine.',
      features: ['Contact direct', 'Réponse < 2h', 'Suivi personnalisé', 'Ligne prioritaire']
    },
    {
      icon: PackageCheck,
      title: 'Contrôle avancé',
      description: 'Vérification approfondie de vos commandes : conformité, quantité, état. Rapport complet avant expédition.',
      features: ['Check-list personnalisée', 'Rapport photographique', 'Validation avant envoi', 'Historique complet']
    }
  ];

  const packagingOptions = [
    {
      icon: Box,
      title: 'Emballage Standard',
      description: 'Protection de base pour vos produits',
      price: 'Inclus',
      features: ['Carton renforcé', 'Papier bulle', 'Calage mousse']
    },
    {
      icon: Gift,
      title: 'Emballage Premium',
      description: 'Protection maximale + présentation soignée',
      price: '+ 5€',
      features: ['Double carton', 'Protection anti-choc', 'Scotch personnalisé', 'Présentation soignée']
    },
    {
      icon: ShieldCheck,
      title: 'Emballage Fragile',
      description: 'Pour vos produits délicats et de valeur',
      price: '+ 8€',
      features: ['Triple protection', 'Caisse bois optionnelle', 'Assurance renforcée', 'Marquage "Fragile"']
    },
    {
      icon: Star,
      title: 'Emballage Personnalisé',
      description: 'Votre branding sur chaque colis',
      price: 'Sur devis',
      features: ['Cartons à votre marque', 'Stickers personnalisés', 'Flyers inclus', 'Message personnalisé']
    }
  ];

  const advantages = [
    { icon: Clock, text: 'Traitement prioritaire de vos colis' },
    { icon: Users, text: 'Interlocuteur dédié à votre compte' },
    { icon: FileText, text: 'Facturation mensuelle simplifiée' },
    { icon: BarChart3, text: 'Rapports et statistiques détaillés' },
    { icon: Settings, text: 'Solutions sur-mesure selon vos besoins' },
    { icon: ShieldCheck, text: 'Assurance renforcée pour vos marchandises' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div {...fadeInUp} className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <Building2 className="w-4 h-4 text-blue-400" />
              <span className="text-blue-300 font-medium text-sm">Offre Professionnelle</span>
            </motion.div>
            
            <motion.h1 
              {...fadeInUp}
              className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white tracking-tight mb-6"
            >
              Solutions logistiques<br />
              <span className="text-blue-400">pour les professionnels</span>
            </motion.h1>
            
            <motion.p 
              {...fadeInUp}
              className="text-xl text-slate-300 max-w-3xl mx-auto mb-8"
            >
              Stockage, gestion des flux, envois express, contrôle qualité... 
              Bénéficiez d'un accompagnement sur-mesure pour développer votre activité à l'international.
            </motion.p>
            
            <motion.div {...fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-bold">
                  Demander un devis gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="tel:+33695148686">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  06 95 14 86 86
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advantages Banner */}
      <section className="py-6 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            {advantages.slice(0, 4).map((adv, index) => (
              <div key={index} className="flex items-center gap-2 text-white">
                <adv.icon className="w-5 h-5 text-blue-200" />
                <span className="text-sm font-medium">{adv.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Nos services professionnels
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des solutions complètes pour optimiser votre logistique et satisfaire vos clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full bg-white border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                      <service.icon className="w-7 h-7 text-blue-600" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-4">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Options d'emballage
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Protégez vos marchandises avec nos solutions d'emballage adaptées à chaque besoin
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packagingOptions.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={`h-full border-2 ${index === 1 ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}>
                  <CardContent className="p-6 text-center">
                    {index === 1 && (
                      <span className="inline-block bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                        Recommandé
                      </span>
                    )}
                    <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-4 ${index === 1 ? 'bg-blue-600' : 'bg-slate-100'}`}>
                      <option.icon className={`w-6 h-6 ${index === 1 ? 'text-white' : 'text-slate-600'}`} />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">
                      {option.title}
                    </h3>
                    <p className="text-sm text-slate-500 mb-3">
                      {option.description}
                    </p>
                    <p className={`font-bold text-lg mb-4 ${index === 1 ? 'text-blue-600' : 'text-slate-900'}`}>
                      {option.price}
                    </p>
                    <ul className="space-y-2 text-left">
                      {option.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle className={`w-4 h-4 flex-shrink-0 ${index === 1 ? 'text-blue-500' : 'text-green-500'}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Pourquoi choisir ISMParcel Pro ?
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Nous accompagnons les e-commerçants, importateurs et entreprises dans leur développement 
                à l'international avec des solutions logistiques sur-mesure.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {advantages.map((adv, index) => (
                  <div key={index} className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-4">
                    <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                      <adv.icon className="w-5 h-5 text-blue-400" />
                    </div>
                    <span className="text-slate-200 font-medium text-sm">{adv.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <h3 className="font-heading font-bold text-2xl mb-4">
                Tarifs préférentiels
              </h3>
              <p className="text-blue-100 mb-6">
                En tant que client professionnel, bénéficiez de :
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-200" />
                  <span>Remises sur volume (à partir de 10 colis/mois)</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-200" />
                  <span>Frais de gestion réduits</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-200" />
                  <span>Stockage sur devis personnalisé</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-200" />
                  <span>Paiement différé (facturation mensuelle)</span>
                </li>
              </ul>
              <a href="#contact">
                <Button className="w-full bg-white text-blue-600 hover:bg-blue-50 font-bold">
                  Obtenir mon tarif personnalisé
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Demandez votre devis gratuit
            </h2>
            <p className="text-lg text-slate-600">
              Décrivez-nous vos besoins et recevez une offre personnalisée sous 24h
            </p>
          </div>
          
          <Card className="bg-white border-slate-200 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company">Nom de l'entreprise *</Label>
                    <Input
                      id="company"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData(p => ({ ...p, company: e.target.value }))}
                      placeholder="Votre entreprise"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Votre nom *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                      placeholder="Prénom Nom"
                      className="h-12"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email professionnel *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                      placeholder="contact@entreprise.com"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                      placeholder="06 00 00 00 00"
                      className="h-12"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="volume">Volume mensuel estimé</Label>
                  <select
                    id="volume"
                    value={formData.volume}
                    onChange={(e) => setFormData(p => ({ ...p, volume: e.target.value }))}
                    className="w-full h-12 px-4 bg-white border border-slate-200 rounded-lg text-slate-900"
                  >
                    <option value="">Sélectionnez une option</option>
                    <option value="1-10">1 à 10 colis/mois</option>
                    <option value="10-50">10 à 50 colis/mois</option>
                    <option value="50-100">50 à 100 colis/mois</option>
                    <option value="100-500">100 à 500 colis/mois</option>
                    <option value="500+">Plus de 500 colis/mois</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Décrivez vos besoins *</Label>
                  <Textarea
                    id="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
                    placeholder="Type de produits, destinations principales, services souhaités (stockage, contrôle qualité, emballage personnalisé...), questions..."
                  />
                </div>
                
                <Button 
                  type="submit" 
                  size="lg"
                  disabled={sending}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-bold"
                >
                  {sending ? 'Envoi en cours...' : 'Envoyer ma demande de devis'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <p className="text-center text-sm text-slate-500">
                  Réponse garantie sous 24h ouvrées
                </p>
              </form>
            </CardContent>
          </Card>
          
          {/* Alternative Contact */}
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Vous préférez nous contacter directement ?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+33695148686"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
              >
                <Phone className="w-5 h-5" />
                06 95 14 86 86
              </a>
              <a 
                href="mailto:contact@ismparcel.fr?subject=Demande d'information - Offre Pro"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Mail className="w-5 h-5" />
                contact@ismparcel.fr
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-3xl text-slate-900 text-center mb-12">
            Questions fréquentes
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: "À partir de quel volume puis-je bénéficier des tarifs Pro ?",
                a: "Nos tarifs professionnels sont accessibles dès 10 colis par mois. Plus votre volume est important, plus vos remises sont avantageuses. Contactez-nous pour une offre personnalisée."
              },
              {
                q: "Comment fonctionne le stockage ?",
                a: "Vos marchandises sont stockées dans notre entrepôt sécurisé à Béziers. Nous pouvons servir de plateforme de stockage pour votre activité. Les tarifs sont établis sur devis selon vos besoins (volume, durée, type de marchandises). Contactez-nous pour une offre personnalisée."
              },
              {
                q: "Proposez-vous la facturation mensuelle ?",
                a: "Oui, les clients professionnels bénéficient d'une facturation mensuelle avec paiement à 30 jours. Vous recevez un récapitulatif détaillé de toutes vos expéditions."
              },
              {
                q: "Puis-je personnaliser mes emballages ?",
                a: "Absolument ! Nous proposons des cartons à votre marque, des stickers personnalisés, l'insertion de flyers ou messages. Contactez-nous pour étudier votre projet."
              },
              {
                q: "Quels sont les délais de traitement pour les pros ?",
                a: "Les clients Pro bénéficient d'un traitement prioritaire. Vos colis sont traités en priorité et peuvent être expédiés le jour même pour les commandes passées avant 14h."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6">
                <h3 className="font-bold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
